import type { ApiKeyUsageDetail, UsageProvider } from './openai-api';
import { hasTrustedPersonOwnerIdentity } from './api-key-identity';

export type DailyCostQuality = 'authoritative' | 'calculated' | 'estimated';
export type DailyIdentityMethod =
  | 'explicit_mapping'
  | 'owner_email'
  | 'provider_owner_id'
  | 'service_account'
  | 'employee_email_key_name'
  | 'unresolved_key';
export type DailyIdentityStatus = 'resolved' | 'service_account' | 'unresolved';

export interface DailyBudgetIdentityMapping {
  /** Optional because an employee directory row can establish an email alias without a key mapping. */
  apiKeyId?: string;
  provider?: UsageProvider;
  employeeId?: string;
  email: string;
  displayName?: string;
}

export interface DailyBudgetSource {
  provider: UsageProvider;
  apiKeyId: string;
  apiKeyName: string;
  sourceIds: string[];
  projectId?: string;
  projectName?: string;
  ownerUserId?: string;
  ownerEmail?: string;
  ownerType?: 'user' | 'service_account';
  cost: number;
  costQuality: DailyCostQuality;
}

export interface DailyUserBudget {
  id: string;
  displayName: string;
  ownerEmail?: string;
  identityMethod: DailyIdentityMethod;
  identityStatus: DailyIdentityStatus;
  actionable: boolean;
  todayCost: number;
  costQuality: DailyCostQuality;
  sources: DailyBudgetSource[];
}

export interface DailyBudgetEvaluation {
  date: string;
  threshold: number;
  validThreshold: boolean;
  budgets: DailyUserBudget[];
  exceeded: DailyUserBudget[];
}

export interface EvaluateDailyUserBudgetsOptions {
  date: string;
  threshold: number;
  /**
   * Explicit employee/key mappings. An employee directory row without apiKeyId
   * also opts that exact email into the key-name convention; partial matching is
   * deliberately never used for budget identity.
   */
  identityMappings?: readonly DailyBudgetIdentityMapping[];
}

interface ResolvedIdentity {
  id: string;
  displayName: string;
  ownerEmail?: string;
  method: DailyIdentityMethod;
  status: DailyIdentityStatus;
}

interface BudgetAccumulator {
  budget: DailyUserBudget;
  costMicroUsd: number;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MICRO_USD_PER_USD = 1_000_000;
const QUALITY_RANK: Record<DailyCostQuality, number> = {
  authoritative: 0,
  calculated: 1,
  estimated: 2
};

export const formatUtcDateKey = (date: Date): string =>
  `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;

const normalizeEmail = (value?: string): string | undefined => {
  const normalized = value?.trim().toLowerCase();
  return normalized && EMAIL_PATTERN.test(normalized) ? normalized : undefined;
};

const getProvider = (entry: ApiKeyUsageDetail): UsageProvider | undefined => {
  if (entry.provider === 'openai' || entry.provider === 'anthropic') {
    return entry.provider;
  }
  return undefined;
};

const sourceKey = (entry: ApiKeyUsageDetail, provider: UsageProvider): string =>
  `${provider}:${entry.projectId ?? ''}:${entry.id}`;

const flattenSources = (entries: ApiKeyUsageDetail[]): ApiKeyUsageDetail[] => {
  const flattened: ApiKeyUsageDetail[] = [];
  const seen = new Set<string>();

  const visit = (entry: ApiKeyUsageDetail): void => {
    if (entry.mergedSources?.length) {
      entry.mergedSources.forEach(visit);
      return;
    }

    const provider = getProvider(entry);
    if (!provider) return;
    const key = sourceKey(entry, provider);
    if (seen.has(key)) return;
    seen.add(key);
    flattened.push(entry);
  };

  entries.forEach(visit);
  return flattened;
};

const buildIdentityIndexes = (mappings: readonly DailyBudgetIdentityMapping[]) => {
  const bySource = new Map<string, DailyBudgetIdentityMapping>();
  const byEmail = new Map<string, DailyBudgetIdentityMapping>();

  for (const mapping of mappings) {
    const email = normalizeEmail(mapping.email);
    if (!email) continue;
    const normalized = { ...mapping, email };
    byEmail.set(email, normalized);
    if (mapping.apiKeyId) {
      bySource.set(`${mapping.provider ?? '*'}:${mapping.apiKeyId}`, normalized);
    }
  }

  return { bySource, byEmail };
};

const mappedIdentity = (
  mapping: DailyBudgetIdentityMapping,
  method: 'explicit_mapping' | 'owner_email' | 'employee_email_key_name'
): ResolvedIdentity => {
  const email = normalizeEmail(mapping.email)!;
  const employeeId = mapping.employeeId?.trim().toLowerCase();
  return {
    id: employeeId ? `employee:${employeeId}` : `email:${email}`,
    displayName: mapping.displayName?.trim() || email,
    ownerEmail: email,
    method,
    status: 'resolved'
  };
};

const resolveIdentity = (
  entry: ApiKeyUsageDetail,
  provider: UsageProvider,
  indexes: ReturnType<typeof buildIdentityIndexes>
): ResolvedIdentity => {
  const explicitMapping = indexes.bySource.get(`${provider}:${entry.id}`)
    ?? indexes.bySource.get(`*:${entry.id}`);
  if (explicitMapping) {
    return mappedIdentity(explicitMapping, 'explicit_mapping');
  }

  // Service accounts must be separated before looking at an email-like key name.
  if (entry.ownerType === 'service_account') {
    const ownerId = entry.ownerUserId || entry.id;
    return {
      id: `${provider}:service_account:${ownerId}`,
      displayName: entry.name || ownerId,
      method: 'service_account',
      status: 'service_account'
    };
  }

  const trustedPersonOwner = hasTrustedPersonOwnerIdentity(entry);
  const ownerEmail = trustedPersonOwner ? normalizeEmail(entry.ownerEmail) : undefined;
  if (ownerEmail) {
    const employee = indexes.byEmail.get(ownerEmail);
    if (employee) return mappedIdentity(employee, 'owner_email');
    return {
      id: `email:${ownerEmail}`,
      displayName: ownerEmail,
      ownerEmail,
      method: 'owner_email',
      status: 'resolved'
    };
  }

  if (trustedPersonOwner && entry.ownerUserId) {
    return {
      id: `${provider}:user:${entry.ownerUserId}`,
      displayName: entry.name || entry.ownerUserId,
      method: 'provider_owner_id',
      status: 'resolved'
    };
  }

  // A regular Anthropic key name is trusted only when it exactly matches an
  // explicitly imported employee email. This avoids unsafe local-part/prefix matching.
  const keyNameEmail = normalizeEmail(entry.name);
  const employee = keyNameEmail ? indexes.byEmail.get(keyNameEmail) : undefined;
  if (employee) {
    return mappedIdentity(employee, 'employee_email_key_name');
  }

  return {
    id: `${provider}:key:${entry.id}`,
    displayName: entry.name || entry.id,
    method: 'unresolved_key',
    status: 'unresolved'
  };
};

const getDailyCost = (entry: ApiKeyUsageDetail, date: string): number => {
  const modelCosts = entry.dailyModelCosts?.[date];
  if (!modelCosts) return 0;

  return Object.values(modelCosts).reduce((sum, value) => {
    return Number.isFinite(value) && value > 0 ? sum + value : sum;
  }, 0);
};

const toMicroUsd = (value: number): number =>
  Math.round(Math.max(0, value) * MICRO_USD_PER_USD);

const fromMicroUsd = (value: number): number => value / MICRO_USD_PER_USD;

const getCostQuality = (entry: ApiKeyUsageDetail, provider: UsageProvider): DailyCostQuality =>
  entry.costQuality ?? (provider === 'anthropic' ? 'estimated' : 'calculated');

const weakerQuality = (left: DailyCostQuality, right: DailyCostQuality): DailyCostQuality =>
  QUALITY_RANK[right] > QUALITY_RANK[left] ? right : left;

/**
 * Evaluate a fixed UTC calendar day independently from the dashboard's visible
 * date range. Raw sources are grouped only by trusted ownership or explicit
 * employee mapping; unresolved keys never merge by display name.
 */
export function evaluateDailyUserBudgets(
  apiKeyUsage: ApiKeyUsageDetail[],
  options: EvaluateDailyUserBudgetsOptions
): DailyBudgetEvaluation {
  const validThreshold = Number.isFinite(options.threshold) && options.threshold >= 0;
  const threshold = validThreshold ? options.threshold : 0;
  const thresholdMicroUsd = toMicroUsd(threshold);
  const indexes = buildIdentityIndexes(options.identityMappings ?? []);
  const budgetMap = new Map<string, BudgetAccumulator>();

  for (const entry of flattenSources(apiKeyUsage)) {
    const provider = getProvider(entry);
    if (!provider) continue;

    const costMicroUsd = toMicroUsd(getDailyCost(entry, options.date));
    // Zero-cost metadata rows are not spend sources for this UTC day. Keeping
    // them would weaken cost quality and attach unrelated action targets.
    if (costMicroUsd === 0) continue;
    const cost = fromMicroUsd(costMicroUsd);
    const quality = getCostQuality(entry, provider);
    const identity = resolveIdentity(entry, provider, indexes);
    const accumulator = budgetMap.get(identity.id) ?? {
      budget: {
        id: identity.id,
        displayName: identity.displayName,
        ownerEmail: identity.ownerEmail,
        identityMethod: identity.method,
        identityStatus: identity.status,
        actionable: identity.status === 'resolved',
        todayCost: 0,
        costQuality: quality,
        sources: []
      },
      costMicroUsd: 0
    };

    accumulator.costMicroUsd += costMicroUsd;
    accumulator.budget.todayCost = fromMicroUsd(accumulator.costMicroUsd);
    accumulator.budget.costQuality = weakerQuality(accumulator.budget.costQuality, quality);
    accumulator.budget.sources.push({
      provider,
      apiKeyId: entry.id,
      apiKeyName: entry.name,
      sourceIds: Array.from(new Set(entry.sourceIds?.length ? entry.sourceIds : [entry.id])),
      projectId: entry.projectId,
      projectName: entry.projectName,
      ownerUserId: entry.ownerUserId,
      ownerEmail: normalizeEmail(entry.ownerEmail) ?? identity.ownerEmail,
      ownerType: entry.ownerType,
      cost,
      costQuality: quality
    });
    budgetMap.set(identity.id, accumulator);
  }

  const accumulators = Array.from(budgetMap.values());
  accumulators.sort((a, b) =>
    b.costMicroUsd - a.costMicroUsd
      || a.budget.displayName.localeCompare(b.budget.displayName)
  );
  const budgets = accumulators.map(({ budget }) => budget);
  const exceeded = validThreshold
    ? accumulators
        .filter(({ costMicroUsd }) => costMicroUsd >= thresholdMicroUsd)
        .map(({ budget }) => budget)
    : [];

  return {
    date: options.date,
    threshold,
    validThreshold,
    budgets,
    exceeded
  };
}
